import React, { useState, useEffect, useCallback } from "react";
import { Printer, Download, Loader2, Smartphone, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getToken } from "@/lib/api";
import { useT } from "@/i18n";
import { toast } from "sonner";

// Languages the cinematic vehicle flyer is fully localized for (see backend _VSTR).
const FLYER_LANGS = [
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "nl", label: "Nederlands", flag: "🇳🇱" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
  { code: "pl", label: "Polski", flag: "🇵🇱" },
  { code: "ro", label: "Română", flag: "🇷🇴" },
];

const FORMATS = [
  { key: "flyer", label: "Flyer A5", sub: "Impression" },
  { key: "story", label: "Story 9:16", sub: "Réseaux" },
];

export default function FlyerStudioDialog({ carId, car = {}, open, onClose }) {
  const { lang: uiLang } = useT();
  const [lang, setLang] = useState(FLYER_LANGS.some((l) => l.code === uiLang) ? uiLang : "fr");
  const [fmt, setFmt] = useState("flyer");
  const [loading, setLoading] = useState(false);
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const title = `${car.make || ""} ${car.model || ""}`.trim() || "Véhicule";

  const load = useCallback(async () => {
    if (!carId) return;
    setLoading(true);
    setBlob(null);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/cars/${carId}/flyer?fmt=${fmt}&lang=${lang}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error(res.status === 403 ? "Réservé au vendeur ou admin" : `Erreur ${res.status}`);
      const b = await res.blob();
      setBlob(b);
      setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(b); });
    } catch (e) {
      toast.error(e.message || "Génération impossible");
      setBlob(null);
    } finally {
      setLoading(false);
    }
  }, [carId, fmt, lang]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const fileName = `pactly-flyer-${carId}-${fmt}-${lang}.png`;

  const download = () => {
    if (!blob) return;
    const obj = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = obj; a.download = fileName;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(obj);
    toast.success("Flyer téléchargé ✓");
  };

  const listingUrl = `${window.location.origin}/car/${carId}`;
  const caption = `${title} — disponible sur Pactly Car`;

  const shareWhatsApp = async () => {
    if (!blob) return;
    const file = new File([blob], fileName, { type: "image/png" });
    // Mobile: native share sheet lets the user send the actual image to WhatsApp.
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: "Pactly Car", text: `${caption}\n${listingUrl}` });
        return;
      } catch (e) {
        if (e && e.name === "AbortError") return;
      }
    }
    // Desktop fallback: download the image and open WhatsApp Web with the listing link.
    download();
    window.open(`https://wa.me/?text=${encodeURIComponent(caption + "\n" + listingUrl)}`, "_blank", "noopener");
    toast.info("Image téléchargée — glissez-la dans la conversation WhatsApp qui vient de s'ouvrir.");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="flyer-studio-dialog" className="max-w-3xl border-white/10 bg-[#0B1A33] text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display font-extrabold">
            <Printer size={18} className="text-pactly-blue" /> Flyer du véhicule — {title}
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Aperçu en direct. Choisissez la langue et le format, puis téléchargez ou partagez sur WhatsApp.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 sm:grid-cols-[1fr_240px]">
          {/* Live preview */}
          <div className="flex min-h-[320px] items-center justify-center rounded-2xl border border-white/10 bg-black/30 p-3">
            {loading ? (
              <div className="flex flex-col items-center gap-2 text-white/60" data-testid="flyer-preview-loading">
                <Loader2 size={26} className="animate-spin text-pactly-blue" />
                <span className="text-sm">Génération de l'aperçu…</span>
              </div>
            ) : previewUrl ? (
              <img data-testid="flyer-preview-img" src={previewUrl} alt="Aperçu du flyer"
                className="max-h-[62vh] w-auto rounded-lg object-contain shadow-2xl" />
            ) : (
              <div className="text-sm text-white/50">Aperçu indisponible.</div>
            )}
          </div>

          {/* Controls */}
          <div className="space-y-4">
            <div>
              <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-white/40">Format</div>
              <div className="grid grid-cols-2 gap-2">
                {FORMATS.map((f) => (
                  <button key={f.key} data-testid={`flyer-fmt-${f.key}`} onClick={() => setFmt(f.key)}
                    className={`rounded-xl border px-2 py-2 text-center transition ${fmt === f.key
                      ? "border-pactly-blue bg-pactly-blue/15 text-white"
                      : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10"}`}>
                    <div className="text-xs font-bold">{f.label}</div>
                    <div className="text-[10px] text-white/45">{f.sub}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-white/40">Langue</div>
              <div className="grid max-h-52 grid-cols-1 gap-1.5 overflow-y-auto pr-1">
                {FLYER_LANGS.map((l) => (
                  <button key={l.code} data-testid={`flyer-lang-${l.code}`} onClick={() => setLang(l.code)}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-left text-sm transition ${lang === l.code
                      ? "border-pactly-blue bg-pactly-blue/15 font-bold text-white"
                      : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10"}`}>
                    <span className="text-base leading-none">{l.flag}</span> {l.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2 pt-1">
              <button data-testid="flyer-share-whatsapp" onClick={shareWhatsApp} disabled={loading || !blob}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-2.5 text-sm font-bold text-black transition hover:brightness-95 disabled:opacity-50">
                <MessageCircle size={16} /> Partager sur WhatsApp
              </button>
              <button data-testid="flyer-download" onClick={download} disabled={loading || !blob}
                className="flex w-full items-center justify-center gap-2 rounded-xl gradient-brand px-4 py-2.5 text-sm font-bold text-white transition hover:opacity-90 disabled:opacity-50">
                <Download size={16} /> Télécharger le flyer
              </button>
              <p className="flex items-center gap-1.5 text-[10px] leading-snug text-white/40">
                <Smartphone size={11} /> Sur mobile, WhatsApp reçoit directement l'image. Sur ordinateur, l'image est téléchargée et WhatsApp Web s'ouvre avec le lien.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
